import { useState } from 'react';
import type { Character } from '@/schemas/character';

/**
 * **FI-46bis** — Hook che incapsula state + helper per l'**edit-mode L1**
 * del `BuilderPage`. Estratto da `BuilderPageInner` per rispettare
 * `max-lines-per-function: 100` e `complexity: 10`.
 *
 * Ritorna:
 *  - `isEditMode`: true se `editFromCharacter` è definito
 *  - `truncatedLevels`: numero di livelli `L2..L{N}` che verranno cancellati
 *    confermando il save dell'edit L1 (0 se PG era già L1)
 *  - `truncateDialogOpen` / `setTruncateDialogOpen`: state del
 *    `<TruncateLevelsDialog>` (mostrato al Finalizza se `truncatedLevels > 0`)
 *  - `performFinalize`: wrapper di `handleFinalize` che apre il dialog
 *    prima del save se `truncatedLevels > 0`; altrimenti chiama il
 *    handler diretto
 *
 * In creazione normale (`editFromCharacter === undefined`) il hook ritorna
 * `isEditMode=false`, `truncatedLevels=0`, e `performFinalize` ===
 * `handleFinalize` diretto (no dialog).
 */
export function useBuilderEditHandlers(
  editFromCharacter: Character | undefined,
  handleFinalize: () => void,
) {
  const isEditMode = editFromCharacter !== undefined;
  const truncatedLevels = editFromCharacter ? editFromCharacter.level - 1 : 0;
  const [truncateDialogOpen, setTruncateDialogOpen] = useState(false);

  function performFinalize(): void {
    if (isEditMode && truncatedLevels > 0) {
      setTruncateDialogOpen(true);
      return;
    }
    handleFinalize();
  }

  function confirmTruncate(): void {
    setTruncateDialogOpen(false);
    handleFinalize();
  }

  return {
    isEditMode,
    truncatedLevels,
    truncateDialogOpen,
    setTruncateDialogOpen,
    performFinalize,
    confirmTruncate,
  };
}
